import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) =>
  keys.map((k) => r?.[k]).find((x) => x !== undefined && x !== null) ?? "";

const safe = (v) =>
  v === null || v === undefined ? "" : typeof v === "object" ? "" : String(v);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const csvCell = (v) => {
  const text = safe(v).replace(/"/g, '""');
  return `"${text}"`;
};

export default function PurchaseRegisterPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState({
    fromDate: today().slice(0, 8) + "01",
    toDate: today(),
    supplierId: "",
    search: "",
  });

  const filteredRows = useMemo(() => {
    const s = filter.search.toLowerCase().trim();
    if (!s) return rows;

    return rows.filter((r) =>
      Object.values(r).map(safe).join(" ").toLowerCase().includes(s),
    );
  }, [rows, filter.search]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (a, r) => ({
        subTotal: a.subTotal + num(val(r, "SubTotal", "subTotal")),
        cgst: a.cgst + num(val(r, "CGSTAmount", "cgstAmount", "CGST", "cgst")),
        sgst: a.sgst + num(val(r, "SGSTAmount", "sgstAmount", "SGST", "sgst")),
        igst: a.igst + num(val(r, "IGSTAmount", "igstAmount", "IGST", "igst")),
        tax: a.tax + num(val(r, "TaxAmount", "taxAmount")),
        discount: a.discount + num(val(r, "DiscountAmount", "discountAmount")),
        grand: a.grand + num(val(r, "GrandTotal", "grandTotal")),
      }),
      {
        subTotal: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        tax: 0,
        discount: 0,
        grand: 0,
      },
    );
  }, [filteredRows]);

  useEffect(() => {
    loadSuppliers();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await client.get("/suppliers", {
        params: { companyId: companyId() || undefined },
      });
      setSuppliers(getArray(res.data));
    } catch {
      setSuppliers([]);
    }
  };

  const load = async () => {
    try {
      setLoading(true);

      const res = await client.get("/sales/purchase-register", {
        params: {
          companyId: companyId() || undefined,
          fromDate: filter.fromDate || undefined,
          toDate: filter.toDate || undefined,
          supplierId: filter.supplierId || undefined,
        },
      });

      setRows(getArray(res.data));
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.title ||
        e?.message ||
        "Failed to load purchase register";

      alert(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const headers = [
      "Voucher No",
      "Date",
      "Supplier",
      "GSTIN",
      "Sub Total",
      "CGST",
      "SGST",
      "IGST",
      "Tax",
      "Discount",
      "Grand Total",
      "ITC Eligibility",
      "Notes",
    ];

    const lines = filteredRows.map((r) =>
      [
        val(r, "VoucherNo", "voucherNo"),
        String(val(r, "VoucherDate", "voucherDate")).slice(0, 10),
        val(r, "SupplierName", "supplierName"),
        val(r, "GSTIN", "gstin"),
        money(val(r, "SubTotal", "subTotal")),
        money(val(r, "CGSTAmount", "cgstAmount", "CGST", "cgst")),
        money(val(r, "SGSTAmount", "sgstAmount", "SGST", "sgst")),
        money(val(r, "IGSTAmount", "igstAmount", "IGST", "igst")),
        money(val(r, "TaxAmount", "taxAmount")),
        money(val(r, "DiscountAmount", "discountAmount")),
        money(val(r, "GrandTotal", "grandTotal")),
        val(r, "ITCEligibility", "itcEligibility") || "CLAIMABLE",
        val(r, "Notes", "notes"),
      ]
        .map(csvCell)
        .join(","),
    );

    const csv = [headers.map(csvCell).join(","), ...lines].join("\n");
    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "purchase-register.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-journal-check me-2"></i>
            Purchase Register
          </h6>
          <div style={{ fontSize: 10, opacity: 0.85 }}>
            Date-wise, supplier-wise and GST/ITC purchase report
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Stat title="Taxable" value={`₹ ${money(totals.subTotal)}`} />
            <Stat title="Input GST" value={`₹ ${money(totals.tax)}`} />
            <Stat title="Discount" value={`₹ ${money(totals.discount)}`} />
            <Stat title="Purchase Total" value={`₹ ${money(totals.grand)}`} />
          </div>

          <div className="row g-2 mb-2">
            <Field label="From">
              <input
                type="date"
                className="form-control form-control-sm"
                value={filter.fromDate}
                onChange={(e) =>
                  setFilter({ ...filter, fromDate: e.target.value })
                }
              />
            </Field>

            <Field label="To">
              <input
                type="date"
                className="form-control form-control-sm"
                value={filter.toDate}
                onChange={(e) =>
                  setFilter({ ...filter, toDate: e.target.value })
                }
              />
            </Field>

            <Field label="Supplier" col="col-md-3">
              <select
                className="form-select form-select-sm"
                value={filter.supplierId}
                onChange={(e) =>
                  setFilter({ ...filter, supplierId: e.target.value })
                }
              >
                <option value="">All Suppliers</option>
                {suppliers.map((s, i) => (
                  <option
                    key={val(s, "Id", "id") || i}
                    value={val(s, "Id", "id")}
                  >
                    {safe(
                      val(s, "SupplierName", "supplierName", "Name", "name"),
                    )}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Search" col="col-md-3">
              <input
                className="form-control form-control-sm"
                value={filter.search}
                placeholder="Voucher / supplier / GSTIN"
                onChange={(e) =>
                  setFilter({ ...filter, search: e.target.value })
                }
              />
            </Field>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={load}
                disabled={loading}
              >
                {loading ? "..." : "Load"}
              </button>
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-success btn-sm w-100"
                onClick={exportCsv}
                disabled={filteredRows.length === 0}
              >
                CSV
              </button>
            </div>
          </div>

          <div
            style={{
              maxHeight: 520,
              overflow: "auto",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1500, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Voucher No",
                    "Date",
                    "Supplier",
                    "GSTIN",
                    "Taxable",
                    "CGST",
                    "SGST",
                    "IGST",
                    "Input GST",
                    "Discount",
                    "Grand Total",
                    "ITC",
                    "Notes",
                  ].map((h) => (
                    <TH key={h}>{h}</TH>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="13"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No purchase records found"}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, i) => (
                    <tr key={val(r, "Id", "id", "VoucherNo", "voucherNo") || i}>
                      <td style={{ fontWeight: 800 }}>
                        {safe(val(r, "VoucherNo", "voucherNo"))}
                      </td>
                      <td>
                        {String(val(r, "VoucherDate", "voucherDate")).slice(
                          0,
                          10,
                        )}
                      </td>
                      <td>{safe(val(r, "SupplierName", "supplierName"))}</td>
                      <td>{safe(val(r, "GSTIN", "gstin")) || "-"}</td>
                      <td className="text-end">
                        {money(val(r, "SubTotal", "subTotal"))}
                      </td>
                      <td className="text-end">
                        {money(
                          val(r, "CGSTAmount", "cgstAmount", "CGST", "cgst"),
                        )}
                      </td>
                      <td className="text-end">
                        {money(
                          val(r, "SGSTAmount", "sgstAmount", "SGST", "sgst"),
                        )}
                      </td>
                      <td className="text-end">
                        {money(
                          val(r, "IGSTAmount", "igstAmount", "IGST", "igst"),
                        )}
                      </td>
                      <td className="text-end">
                        {money(val(r, "TaxAmount", "taxAmount"))}
                      </td>
                      <td className="text-end">
                        {money(val(r, "DiscountAmount", "discountAmount"))}
                      </td>
                      <td className="text-end fw-bold">
                        {money(val(r, "GrandTotal", "grandTotal"))}
                      </td>
                      <td>
                        {safe(val(r, "ITCEligibility", "itcEligibility")) ||
                          "CLAIMABLE"}
                      </td>
                      <td>{safe(val(r, "Notes", "notes")) || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot>
                <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                  <td colSpan="4" className="text-end">
                    Total
                  </td>
                  <td className="text-end">{money(totals.subTotal)}</td>
                  <td className="text-end">{money(totals.cgst)}</td>
                  <td className="text-end">{money(totals.sgst)}</td>
                  <td className="text-end">{money(totals.igst)}</td>
                  <td className="text-end">{money(totals.tax)}</td>
                  <td className="text-end">{money(totals.discount)}</td>
                  <td className="text-end">{money(totals.grand)}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label style={{ fontSize: 10, fontWeight: 700, marginBottom: 3 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>
        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function TH({ children }) {
  return (
    <th
      style={{
        fontSize: 10,
        color: "#fff",
        background: "#07111f",
        padding: "7px 8px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      {children}
    </th>
  );
}
